import { FC, useEffect } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import useFetchCurrentUser from '@components/features/currentUser/hooks/useFetchCurrentUser';
import Heading from '@components/primitives/heading/Heading';
import Layout from '@components/primitives/layout/Layout';
import Content from '@components/primitives/content/Content';
import PageHead from '@components/primitives/pageHead/PageHead';
import PageContent from '@components/primitives/pageContent/PageContent';
import { useTranslation } from 'react-i18next';
import usePageTitle from '@hooks/usePageTitle';
import { PagePath } from '@routing/appUrls';
import * as Sentry from '@sentry/react';

const RootErrorBoundary: FC = () => {
    const error = useRouteError() as Error;
    const navigate = useNavigate();
    const { data: user } = useFetchCurrentUser();
    const { t } = useTranslation();
    const title = error?.message || t('errorBoundary.title');
    const content = error?.stack || t('errorBoundary.message');
    usePageTitle(title);

    const isRouteError = isRouteErrorResponse(error);

    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    useEffect(() => {
        if (isRouteError) {
            navigate(PagePath.dashboard);
        }
    }, [isRouteError]);

    return (
        <>
            {!user ? (
                <>
                    <div>
                        <Heading>{t('errorBoundary.title')}</Heading>
                        <strong>{title}</strong>
                        <br />
                        <br />
                        <code>{content}</code>
                    </div>
                </>
            ) : (
                <div className="app">
                    <Layout>
                        <Content isLight>
                            <PageHead title={t('errorBoundary.title')} />
                            <PageContent>
                                <strong>{title}</strong>
                                <br />
                                <br />
                                <code>{content}</code>
                            </PageContent>
                        </Content>
                    </Layout>
                </div>
            )}
        </>
    );
};

export default RootErrorBoundary;
